import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { el, enUS } from 'date-fns/locale';
import {
  Calendar, Clock, Users, Euro, Loader2, 
  Video, MapPin, ChevronRight, Ticket
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import Navbar from '../components/Navbar';
import { resolveMediaUrl } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeminarsPage() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [seminars, setSeminars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [selectedSeminar, setSelectedSeminar] = useState(null);
  const token = localStorage.getItem('token');
  const isEl = i18n.language === 'el';
  const labels = {
    details: isEl ? 'Πληροφορίες' : 'Details',
    bookSeat: isEl ? 'Κλείσε Θέση' : 'Book Seat',
    learn: isEl ? 'Τι θα μάθεις' : 'What you will learn',
    includes: isEl ? 'Περιλαμβάνει' : 'Includes',
    who: isEl ? 'Σε ποιους απευθύνεται' : 'Who it is for',
    outcome: isEl ? 'Αποτέλεσμα' : 'Outcome',
    instructorBio: isEl ? 'Bio εκπαιδευτή' : 'Instructor bio',
    duration: isEl ? 'Διάρκεια' : 'Duration',
    online: isEl ? 'Online (Zoom)' : 'Online (Zoom)',
    free: isEl ? 'Δωρεάν' : 'Free'
  };

  const asList = (value) => Array.isArray(value)
    ? value.filter(Boolean)
    : String(value || '').split('\n').map((item) => item.trim()).filter(Boolean);

  const seminarImageUrl = (seminar) => (
    seminar?.cover_image_url || seminar?.image_url || seminar?.promo_image_url || ''
  );

  useEffect(() => {
    fetchSeminars();
  }, []);

  const fetchSeminars = async () => {
    try {
      const res = await axios.get(`${API}/seminars?locale=${i18n.language}`);
      setSeminars(res.data);
    } catch (e) {
      console.error('Failed to load seminars:', e);
      toast.error(isEl ? 'Σφάλμα φόρτωσης σεμιναρίων' : 'Failed to load seminars');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (seminarId) => {
    if (!token) {
      toast.info(isEl ? 'Παρακαλώ συνδεθείτε για να κλείσετε θέση' : 'Please login to book a seat');
      navigate('/login');
      return;
    }

    setCheckoutLoading(seminarId);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/seminars/${seminarId}/checkout`, {}, { headers });
      
      if (res.data.free) {
        toast.success(isEl ? 'Επιτυχής εγγραφή!' : 'Successfully registered!');
        navigate('/my-seminars');
      } else if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === 'You already have a ticket for this seminar') {
        toast.info(isEl ? 'Έχετε ήδη εισιτήριο για αυτό το σεμινάριο' : detail);
        navigate('/my-seminars');
      } else {
        toast.error(detail || (isEl ? 'Σφάλμα κράτησης' : 'Booking failed'));
      }
    } finally {
      setCheckoutLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'EEEE, dd MMMM yyyy', { locale: isEl ? el : enUS });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'HH:mm');
    } catch {
      return dateStr;
    }
  };

  const seminarMediaType = (seminar) => {
    const mediaType = (seminar.media_type || '').toLowerCase();
    if (mediaType === 'video' && seminar.video_url) return 'video';
    return 'image';
  };

  const isUpcoming = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) > new Date();
  };

  const isLive = (startStr, durationMinutes) => {
    if (!startStr) return false;
    const start = new Date(startStr);
    const end = new Date(start.getTime() + (durationMinutes || 60) * 60 * 1000);
    const now = new Date();
    return now >= start && now <= end;
  };

  return (
    <div className="min-h-screen bg-misel-dark">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-primary/10 to-transparent py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {isEl ? 'Live Σεμινάρια' : 'Live Seminars'}
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {isEl 
              ? 'Μάθε από τους καλύτερους επαγγελματίες σε real-time sessions' 
              : 'Learn from the best professionals in real-time sessions'}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-primary" size={32} />
          </div>
        ) : seminars.length === 0 ? (
          <div className="text-center py-16">
            <Calendar className="mx-auto mb-4 text-gray-500" size={48} />
            <h2 className="text-2xl font-bold text-white mb-2">
              {isEl ? 'Δεν υπάρχουν διαθέσιμα σεμινάρια' : 'No seminars available'}
            </h2>
            <p className="text-gray-400">
              {isEl ? 'Νέα σεμινάρια θα ανακοινωθούν σύντομα!' : 'New seminars will be announced soon!'}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {seminars.map((seminar) => {
              const upcoming = isUpcoming(seminar.start_at);
              const live = isLive(seminar.start_at, seminar.duration_minutes);
              const isFull = (seminar.available_seats || 0) <= 0;
              const learnItems = asList(seminar.what_you_will_learn);
              const cardDescription = seminar.short_description || seminar.description;
              const ctaLabel = seminar.cta_label || labels.bookSeat;
              const cardImageUrl = seminarImageUrl(seminar);

              return (
                <div 
                  key={seminar.id}
                  className={`bg-misel-card border rounded-sm overflow-hidden transition-all hover:border-primary/50 ${
                    live ? 'border-green-500/50 ring-1 ring-green-500/20' : 'border-white/10'
                  }`}
                >
                  {/* Media Preview (image/video switch per seminar) */}
                  {(cardImageUrl || seminar.video_url) && (
                    <div className="relative h-48 overflow-hidden" data-testid={`seminar-media-container-${seminar.id}`}>
                      {seminarMediaType(seminar) === 'video' && seminar.video_url ? (
                        <video
                          data-testid={`seminar-video-preview-${seminar.id}`}
                          src={resolveMediaUrl(seminar.video_url)}
                          className="w-full h-full object-cover"
                          muted
                          autoPlay
                          loop
                          playsInline
                          controls={false}
                        />
                      ) : (
                        <img
                          data-testid={`seminar-image-preview-${seminar.id}`}
                          src={resolveMediaUrl(cardImageUrl)}
                          alt={seminar.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {live && (
                        <div className="absolute top-3 left-3">
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-500 text-white animate-pulse flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-white"></span>
                            LIVE
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="p-5">
                    {/* Date & Status */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 text-sm text-primary">
                        <Calendar size={14} />
                        {formatDate(seminar.start_at)}
                      </div>
                      {!upcoming && !live && (
                        <span className="text-xs text-gray-500">
                          {isEl ? 'Ολοκληρώθηκε' : 'Completed'}
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">
                      {seminar.title}
                    </h3>

                    {/* Description */}
                    {cardDescription && (
                      <p className="text-sm text-gray-400 mb-4 whitespace-pre-line break-words">
                        {cardDescription}
                      </p>
                    )}

                    {learnItems.length > 0 && (
                      <ul className="space-y-1 text-sm text-gray-300 mb-4">
                        {learnItems.slice(0, 3).map((item, index) => (
                          <li key={`${seminar.id}-learn-${index}`} className="flex gap-2">
                            <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-primary" />
                            <span className="break-words">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Meta Info */}
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {formatTime(seminar.start_at)} ({seminar.duration_minutes} min)
                      </span>
                      {seminar.instructor && (
                        <span className="flex items-center gap-1">
                          <Users size={12} />
                          {seminar.instructor}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Video size={12} />
                        {labels.online}
                      </span>
                    </div>

                    {/* Seats Available */}
                    {upcoming && (
                      <div className="flex items-center justify-between text-sm mb-4">
                        <span className={`${isFull ? 'text-red-400' : 'text-gray-400'}`}>
                          {isFull 
                            ? (isEl ? 'Εξαντλήθηκαν οι θέσεις' : 'Sold out')
                            : `${seminar.available_seats} ${isEl ? 'θέσεις διαθέσιμες' : 'seats available'}`
                          }
                        </span>
                      </div>
                    )}

                    {/* Price & CTA */}
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-white/10">
                      <div className="text-white">
                        {seminar.is_free ? (
                          <span className="text-lg font-bold text-green-400">
                            {isEl ? 'Δωρεάν' : 'Free'}
                          </span>
                        ) : (
                          <span className="text-lg font-bold">
                            €{seminar.price}
                          </span>
                        )}
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setSelectedSeminar(seminar)}
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        {labels.details}
                      </Button>

                      {upcoming && !isFull && (
                        <Button
                          onClick={() => handleCheckout(seminar.id)}
                          disabled={checkoutLoading === seminar.id}
                          title={ctaLabel}
                          className="bg-primary hover:bg-primary/90"
                        >
                          {checkoutLoading === seminar.id ? (
                            <Loader2 size={16} className="animate-spin mr-2" />
                          ) : (
                            <Ticket size={16} className="mr-2" />
                          )}
                          {isEl ? 'Κλείσε Θέση' : 'Book Now'}
                        </Button>
                      )}

                      {live && (
                        <span className="text-green-400 text-sm font-medium animate-pulse">
                          {isEl ? 'Σε εξέλιξη...' : 'In progress...'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* My Seminars Link */}
        {token && (
          <div className="mt-12 text-center">
            <Button
              variant="outline"
              onClick={() => navigate('/my-seminars')}
              className="border-primary/50 text-primary hover:bg-primary/10"
            >
              <Ticket size={16} className="mr-2" />
              {isEl ? 'Τα Σεμινάριά μου' : 'My Seminars'}
              <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        )}

        <Dialog open={!!selectedSeminar} onOpenChange={(open) => !open && setSelectedSeminar(null)}>
          <DialogContent className="bg-misel-card border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedSeminar && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl break-words">{selectedSeminar.title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-5">
                  {seminarImageUrl(selectedSeminar) && (
                    <img
                      src={resolveMediaUrl(seminarImageUrl(selectedSeminar))}
                      alt={selectedSeminar.title}
                      className="w-full max-h-80 rounded-sm object-cover border border-white/10"
                    />
                  )}

                  {asList(selectedSeminar.gallery_image_urls).length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {asList(selectedSeminar.gallery_image_urls).map((url, index) => (
                        <img
                          key={`seminar-gallery-${index}`}
                          src={resolveMediaUrl(url)}
                          alt={`${selectedSeminar.title} ${index + 1}`}
                          className="h-20 w-full rounded-sm object-cover border border-white/10"
                        />
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3 text-sm text-gray-300">
                    <span className="flex items-center gap-2">
                      <Calendar size={16} className="text-primary" />
                      {formatDate(selectedSeminar.start_at)}
                    </span>
                    <span className="flex items-center gap-2">
                      <Clock size={16} className="text-primary" />
                      {formatTime(selectedSeminar.start_at)} · {labels.duration} {selectedSeminar.duration_minutes} min
                    </span>
                    {selectedSeminar.instructor && (
                      <span className="flex items-center gap-2">
                        <Users size={16} className="text-primary" />
                        {selectedSeminar.instructor}
                      </span>
                    )}
                    <span className="flex items-center gap-2">
                      <Euro size={16} className="text-primary" />
                      {selectedSeminar.is_free ? labels.free : `€${selectedSeminar.price}`}
                    </span>
                  </div>

                  {(selectedSeminar.full_description || selectedSeminar.description) && (
                    <p className="text-gray-300 whitespace-pre-line break-words leading-relaxed">
                      {selectedSeminar.full_description || selectedSeminar.description}
                    </p>
                  )}

                  {asList(selectedSeminar.what_you_will_learn).length > 0 && (
                    <section>
                      <h3 className="text-sm font-semibold text-white mb-2">{labels.learn}</h3>
                      <ul className="space-y-2 text-gray-300">
                        {asList(selectedSeminar.what_you_will_learn).map((item, index) => (
                          <li key={`modal-learn-${index}`} className="flex gap-2">
                            <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-primary" />
                            <span className="break-words">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {selectedSeminar.who_is_it_for && (
                    <section>
                      <h3 className="text-sm font-semibold text-white mb-2">{labels.who}</h3>
                      <p className="text-gray-300 whitespace-pre-line break-words">{selectedSeminar.who_is_it_for}</p>
                    </section>
                  )}

                  {asList(selectedSeminar.includes).length > 0 && (
                    <section>
                      <h3 className="text-sm font-semibold text-white mb-2">{labels.includes}</h3>
                      <ul className="space-y-2 text-gray-300">
                        {asList(selectedSeminar.includes).map((item, index) => (
                          <li key={`modal-includes-${index}`} className="flex gap-2">
                            <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-primary" />
                            <span className="break-words">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {selectedSeminar.outcome && (
                    <section>
                      <h3 className="text-sm font-semibold text-white mb-2">{labels.outcome}</h3>
                      <p className="text-gray-300 whitespace-pre-line break-words">{selectedSeminar.outcome}</p>
                    </section>
                  )}

                  {selectedSeminar.instructor_bio && (
                    <section className="flex flex-col sm:flex-row gap-4">
                      {selectedSeminar.instructor_image_url && (
                        <img
                          src={resolveMediaUrl(selectedSeminar.instructor_image_url)}
                          alt={selectedSeminar.instructor || labels.instructorBio}
                          className="h-24 w-24 flex-none rounded-sm object-cover border border-white/10"
                        />
                      )}
                      <div>
                        <h3 className="text-sm font-semibold text-white mb-2">{labels.instructorBio}</h3>
                        <p className="text-gray-300 whitespace-pre-line break-words">{selectedSeminar.instructor_bio}</p>
                      </div>
                    </section>
                  )}

                  {isUpcoming(selectedSeminar.start_at) && (selectedSeminar.available_seats || 0) > 0 && (
                    <Button
                      onClick={() => handleCheckout(selectedSeminar.id)}
                      disabled={checkoutLoading === selectedSeminar.id}
                      className="w-full sm:w-auto bg-primary hover:bg-primary/90"
                    >
                      {checkoutLoading === selectedSeminar.id ? (
                        <Loader2 size={16} className="animate-spin mr-2" />
                      ) : (
                        <Ticket size={16} className="mr-2" />
                      )}
                      {selectedSeminar.cta_label || labels.bookSeat}
                    </Button>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
